/**
 * Email reading API — TASK-808.
 *
 * The mailbox Kielis already indexes, for the canvas tile. Connection STATUS deliberately
 * does not live here: `emailConnectionService.getStatus()` (TASK-270) already owns that route
 * and the tile reuses it, so there is exactly one path answering "is a mailbox connected".
 *
 * DEC-1 — every fetch that puts correspondence on the advisor's screen writes an audit row.
 * That is enforced server-side (both routes below go through the audited discover/retrieve
 * bodies), which is why callers must not fetch speculatively: a poll, a prefetch, or a
 * remount writes rows for correspondence nobody looked at and destroys the trail's meaning.
 */

import { API_CONFIG } from '../../config/api-config';
import { httpClient } from '../../lib/httpClient';

export interface EmailCandidate {
  email_id: string;
  sender: string | null;
  subject: string | null;
  snippet: string;
  date: string | null;
  source_ref: string;
  /**
   * The client this mail came FROM, when the sender matches a client record. Resolved
   * server-side and account-scoped (TASK-815) — an address from the message itself must
   * never pick the reply target. Absent for anyone not in the book.
   */
  client_id?: string;
  client_name?: string;
}

export interface EmailListResult {
  emails: EmailCandidate[];
  next_cursor: string | null;
  total_estimate: number;
  /** The named client could not be resolved in this account — the server refused to fall
   *  back to an unfiltered mailbox, so this is NOT "they have no email". */
  unresolved_client?: boolean;
}

export interface EmailBody {
  email_id: string;
  sender: string | null;
  subject: string | null;
  date: string | null;
  body_text: string;
  /**
   * The provider's own markup, when the message has an HTML part. UNTRUSTED third-party
   * content (TASK-622/623) — sanitise before rendering and render it isolated. Null for a
   * plain-text message, which is why `body_text` is always populated too.
   */
  body_html: string | null;
  has_attachments: boolean;
  source_ref: string;
}

/**
 * Candidates only — sender, subject, snippet, date. Never bodies.
 *
 * `clientId` narrows to one client's correspondence. There is no `sender` parameter by
 * design: the address is resolved from the client record server-side, so no address chosen
 * in the browser or by a model can reach the filter.
 */
export async function listEmails(params: {
  clientId?: string;
  keyword?: string;
  limit?: number;
}): Promise<EmailListResult> {
  return httpClient.post<EmailListResult>(
    `${API_CONFIG.GATEWAY_URL}/api/search/email/list`,
    {
      client_id: params.clientId ?? null,
      keyword: params.keyword ?? null,
      limit: params.limit ?? 20,
    },
  );
}

/**
 * ONE email's body, re-fetched from the provider at this moment — TASK-466 keeps bodies out
 * of the index, so there is nothing stored to read. Call it only when the advisor actually
 * opens a row: it is a live provider round-trip and it writes an `EMAIL_MESSAGE_READ` row.
 */
export async function getEmailBody(emailId: string): Promise<EmailBody> {
  return httpClient.get<EmailBody>(
    `${API_CONFIG.GATEWAY_URL}/api/search/email/message`,
    { params: { email_id: emailId } },
  );
}
